import { useAuth } from "@/components/auth/auth-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/components/ui/use-toast";
import { useAbility } from "@/lib/casl/ability-context";
import type { MentorStoryImage } from "@/lib/api/mentor-stories";
import { useRemovePublishedStory } from "@/lib/api/mentor-story-review";
import { MessageContext } from "@/lib/api/onboarding-messages";
import { useMessages } from "@/lib/hooks/onboarding-messages/use-onboarding-messages";
import { useMentorStories } from "@/lib/hooks/mentor-stories/use-mentor-stories";
import { useMentorStoriesEnabled } from "@/lib/hooks/use-mentor-stories";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { Button, IconButton, Skeleton } from "@somethings-ui/web";
import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";
import { FirstMomentsMessages } from "./first-moments-messages";
import { PublishedStory } from "./stories/story-section";
import {
  formatMessageCount,
  onboardingMessagesForDisplay,
} from "./first-moments-summaries";
import { cn } from "@/lib/utils";

type OpenRow = "around" | "away" | null;

interface FirstMomentsRecordRowsProps {
  userId: string;
  mentorName: string;
}

export function FirstMomentsRecordRows({
  userId,
  mentorName,
}: FirstMomentsRecordRowsProps) {
  const [openRow, setOpenRow] = useState<OpenRow>(null);
  const onlineQuery = useMessages(userId, MessageContext.ONLINE);
  const offlineQuery = useMessages(userId, MessageContext.OFFLINE);
  const storiesEnabled = useMentorStoriesEnabled();
  const { data: story, isPending: storyPending } = useMentorStories(
    userId,
    storiesEnabled,
  );

  const online = onboardingMessagesForDisplay(
    onlineQuery.isSuccess ? (onlineQuery.data ?? []) : undefined,
    MessageContext.ONLINE,
  );
  const offline = onboardingMessagesForDisplay(
    offlineQuery.isSuccess ? (offlineQuery.data ?? []) : undefined,
    MessageContext.OFFLINE,
  );
  const slides = story?.images ?? [];

  const toggle = (row: Exclude<OpenRow, null>) => {
    setOpenRow((current) => (current === row ? null : row));
  };

  return (
    <div className="w-full max-w-[900px]">
      <h2 className="font-heading text-header-5 text-text-primary">
        First moments
      </h2>
      <p className="mt-xs font-body text-body-3 font-medium text-text-tertiary">
        Edit the messages teens get first. The story is the published one.
      </p>

      <div className="mt-xl overflow-hidden rounded-xl border border-text-placeholder bg-surfaces-z0 px-xl py-sm">
        <FirstMomentsRow
          label="Online messages"
          summary={formatMessageCount(online.messages.length)}
          preview={online.messages[0]?.message}
          muted={!online.usingDefault && online.messages.length === 0}
          open={openRow === "around"}
          onToggle={() => toggle("around")}
        >
          <FirstMomentsMessages
            userId={userId}
            context={MessageContext.ONLINE}
          />
        </FirstMomentsRow>
        <FirstMomentsRow
          label="Offline messages"
          summary={formatMessageCount(offline.messages.length)}
          preview={offline.messages[0]?.message}
          muted={!offline.usingDefault && offline.messages.length === 0}
          open={openRow === "away"}
          onToggle={() => toggle("away")}
        >
          <FirstMomentsMessages
            userId={userId}
            context={MessageContext.OFFLINE}
          />
        </FirstMomentsRow>
      </div>
      {storiesEnabled ? (
        <RecordStories
          mentorId={userId}
          mentorName={mentorName}
          slides={slides}
          pending={storyPending}
        />
      ) : null}
    </div>
  );
}

function RecordStories({
  mentorId,
  mentorName,
  slides,
  pending,
}: {
  mentorId: string;
  mentorName: string;
  slides: MentorStoryImage[];
  pending: boolean;
}) {
  const [open, setOpen] = useState(true);
  const ability = useAbility();
  const { user } = useAuth();
  const { toast } = useToast();
  const { track } = useTrackEvent();
  const remove = useRemovePublishedStory(mentorId);
  const [confirming, setConfirming] = useState(false);
  const canRemove = ability.can("update", "users") && slides.length > 0;

  const confirmRemove = async () => {
    try {
      await remove.mutateAsync();
      track(PostHogEvents.ADMIN_STORY_ADMIN_DELETED_STORY, {
        admin_id: user?.id,
        admin_role: user?.roleNames?.[0],
        mentor_id: mentorId,
      });
      toast({
        title: `${mentorName}'s story has been removed. Please contact them to create a new story.`,
      });
      setConfirming(false);
    } catch {
      toast({ title: "Could not remove the story", variant: "destructive" });
    }
  };

  return (
    <section className="mt-sm rounded-2xl border border-surfaces-z5 bg-surfaces-z0 px-2xl pb-2xl pt-xl">
      <div className="flex items-start justify-between gap-lg">
        <div>
          <h3 className="font-heading text-header-4 text-text-primary">
            Stories
          </h3>
          <p className="mt-xs font-body text-body-1 text-text-secondary">
            The stories that teens tap through after matching to get to their
            mentor more.
          </p>
        </div>
        <IconButton
          aria-expanded={open}
          aria-label={open ? "Collapse stories" : "Expand stories"}
          variant="header-ghost"
          size="sm"
          icon={
            <ChevronDown
              className={cn("size-5 text-text-primary", open && "rotate-180")}
            />
          }
          onClick={() => setOpen((current) => !current)}
        />
      </div>
      {open ? (
        <div className="mt-xl flex flex-col gap-xl">
          {pending ? (
            <Skeleton className="h-[87px] w-full rounded-2xl" />
          ) : (
            <PublishedStory images={slides} />
          )}
          {canRemove ? (
            <div className="border-t border-surfaces-z5 pt-xl">
              <p className="font-body text-body-1 text-text-secondary">
                Removing the live story takes it down for teens straight away.
                This can&apos;t be undone.
              </p>
              <Button
                size="sm"
                variant="destructive"
                fullWidth={false}
                className="mt-md"
                leadingIcon={<Trash2 className="size-5" />}
                onPress={() => setConfirming(true)}
              >
                Remove story
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Story</DialogTitle>
            <DialogDescription>
              Are you sure? Removing this story will delete it permanently. Teens
              will no longer see a story for this mentor.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              size="sm"
              variant="secondary"
              fullWidth={false}
              disabled={remove.isPending}
              onPress={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="destructive"
              fullWidth={false}
              disabled={remove.isPending}
              loading={remove.isPending}
              onPress={() => void confirmRemove()}
            >
              Remove Story
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function FirstMomentsRow({
  label,
  summary,
  preview,
  muted = false,
  open,
  onToggle,
  children,
}: {
  label: string;
  summary: string;
  preview?: string;
  muted?: boolean;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  if (!open) {
    return (
      <button
        type="button"
        className="flex w-full min-h-[52px] items-center gap-3xl border-b border-surfaces-z5 py-lg text-left last:border-b-0"
        onClick={onToggle}
        aria-expanded={false}
      >
        <p className="w-[116px] shrink-0 font-body text-body-3 font-medium text-text-tertiary">
          {label}
        </p>
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              "font-body text-body-1",
              muted ? "text-text-tertiary" : "text-text-primary",
            )}
          >
            {summary}
          </p>
          {preview ? (
            <p className="mt-xs truncate font-body text-body-3 text-text-tertiary">
              {preview}
            </p>
          ) : null}
        </div>
        <ChevronRight className="size-3.5 shrink-0 text-text-secondary" />
      </button>
    );
  }

  return (
    <div className="flex items-start gap-3xl border-b border-surfaces-z5 py-lg last:border-b-0">
      <p className="w-[116px] shrink-0 pt-xs font-body text-body-3 font-medium text-text-tertiary">
        {label}
      </p>
      <div className="min-w-0 flex-1">{children}</div>
      <IconButton
        aria-expanded
        aria-label={`Close ${label}`}
        variant="header-ghost"
        size="sm"
        className="shrink-0"
        icon={<ChevronRight className="size-3.5 text-text-secondary" />}
        onClick={onToggle}
      />
    </div>
  );
}
